// Considered: the current TabView shell — rejected because TabView is a horizontal tab bar; the design is a persistent 240px left-rail with section grouping, status banners, and a CAUTION badge — a different layout primitive. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { Channel } from 'types/channel';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import './ChannelManageNavRailMolecule.scss';

export type ChannelManageSection =
  | 'profile'
  | 'items'
  | 'branding'
  | 'audience'
  | 'reviews'
  | 'verification'
  | 'danger';

const ALL_SECTIONS: ReadonlyArray<{
  id: ChannelManageSection;
  icon: string;
  marketplaceOnly?: boolean;
  danger?: boolean;
}> = [
  { id: 'profile', icon: 'pi-user' },
  { id: 'items', icon: 'pi-th-large' },
  { id: 'branding', icon: 'pi-palette' },
  { id: 'audience', icon: 'pi-users' },
  { id: 'reviews', icon: 'pi-comment', marketplaceOnly: true },
  { id: 'verification', icon: 'pi-shield', marketplaceOnly: true },
  { id: 'danger', icon: 'pi-exclamation-triangle', danger: true },
];

export interface ChannelManageNavRailMoleculeProps {
  channel: Channel;
  activeSection: ChannelManageSection;
  onSectionChange: (section: ChannelManageSection) => void;
  className?: string;
}

const ChannelManageNavRailMolecule: React.FC<ChannelManageNavRailMoleculeProps> = ({
  channel,
  activeSection,
  onSectionChange,
  className = '',
}) => {
  const { t } = useTranslation();

  const visibleSections = ALL_SECTIONS.filter((s) => {
    if (s.marketplaceOnly && channel.scope !== 'metasphere') return false;
    return true;
  });

  const classes = [
    'channel-manage-nav-rail',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={classes} aria-label={t('marketplace.channelManage.navRail.ariaLabel')}>
      {channel.status === 'pending_review' && (
        <div className="channel-manage-nav-rail__banner channel-manage-nav-rail__banner--pending">
          <span className="channel-manage-nav-rail__banner-title">
            <i className="pi pi-clock channel-manage-nav-rail__banner-icon" aria-hidden="true" />
            {t('marketplace.channelManage.navRail.banners.pendingReviewTitle')}
          </span>
          <span className="channel-manage-nav-rail__banner-note">
            {t('marketplace.channelManage.navRail.banners.pendingReviewNote')}
          </span>
        </div>
      )}
      {channel.status === 'rejected' && (
        <div className="channel-manage-nav-rail__banner channel-manage-nav-rail__banner--rejected">
          <span className="channel-manage-nav-rail__banner-title">
            <i className="pi pi-times-circle channel-manage-nav-rail__banner-icon" aria-hidden="true" />
            {t('marketplace.channelManage.navRail.banners.rejectedTitle')}
          </span>
          <span className="channel-manage-nav-rail__banner-note">
            {t('marketplace.channelManage.navRail.banners.rejectedNote')}
          </span>
        </div>
      )}

      <nav className="channel-manage-nav-rail__nav">
        {visibleSections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <button
              key={section.id}
              type="button"
              className={[
                'channel-manage-nav-rail__item',
                isActive ? 'channel-manage-nav-rail__item--active' : '',
                section.danger ? 'channel-manage-nav-rail__item--danger' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSectionChange(section.id)}
              aria-pressed={isActive}
            >
              <i
                className={`pi ${section.icon} channel-manage-nav-rail__item-icon`}
                aria-hidden="true"
              />
              <span className="channel-manage-nav-rail__item-label">
                {t(`marketplace.channelManage.navRail.sections.${section.id}`)}
              </span>
              {section.danger && (
                <StatusBadgeAtom
                  tone="danger"
                  label={t('marketplace.channelManage.navRail.caution')}
                  size="small"
                  className="channel-manage-nav-rail__caution-badge"
                />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

ChannelManageNavRailMolecule.displayName = 'ChannelManageNavRailMolecule';

export default ChannelManageNavRailMolecule;
